import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';

/**
 * Custom hook for handling common React Query patterns
 * Provides consistent error handling and authentication redirects
 */

// Common query options for data fetching
export const createQueryOptions = (queryKey, queryFn, options = {}) => ({
  queryKey,
  queryFn,
  retry: 2,
  staleTime: 5 * 60 * 1000, // 5 minutes
  ...options
});

// Common mutation options for data modifications
export const createMutationOptions = (mutationFn, options = {}) => ({
  mutationFn,
  ...options
});

// Hook for handling authentication errors consistently
export const useAuthErrorHandler = () => {
  const history = useHistory();
  
  return (error) => {
    console.log('API Error:', error);
    if (error.response && error.response.status === 403) {
      history.push('/sign-in');
    }
  };
};

// Hook for common query patterns with auth error handling
export const useAuthenticatedQuery = (queryKey, queryFn, options = {}) => {
  const handleAuthError = useAuthErrorHandler();
  
  return useQuery({
    ...createQueryOptions(queryKey, queryFn, options),
    onError: (error) => {
      handleAuthError(error);
      options.onError?.(error);
    }
  });
};

// Hook for common mutation patterns with auth error handling
export const useAuthenticatedMutation = (mutationFn, options = {}) => {
  const handleAuthError = useAuthErrorHandler();
  
  return useMutation({
    ...createMutationOptions(mutationFn, options),
    onError: (error) => {
      handleAuthError(error);
      options.onError?.(error);
    }
  });
};

// Hook for invalidating queries after mutations
export const useInvalidateQueries = () => {
  const queryClient = useQueryClient();
  
  return (queryKey) => {
    queryClient.invalidateQueries({ queryKey });
  };
};

